<template>
  <div id="prevent-reports">
    <v-card>
      <div class="pa-5">
        <v-row class="align-sm-center">
          <v-col cols="12">
            <v-card-title class="pa-0">Prevent Reports</v-card-title>
          </v-col>
        </v-row>
      </div>

      <v-divider />

      <v-data-table
        :items="merchantItems"
        :headers="headers"
        :expanded.sync="expandedItems"
        :loading="loading"
        :items-per-page="-1"
        hide-default-footer
        class="prevent-report-table elevation-1"
        item-key="companyId"
        show-expand
        :single-expand="false"
        disable-sort
      >
        <template v-slot:item="{ item, expand, isExpanded }">
          <tr
            :key="`total-${item.companyId}-header`"
            class="prevent-report-table__total-row prevent-report-table__row-group-start actionable"
            @click="expand(!isExpanded)"
          >
            <td class="prevent-report-table__expand-col">
              <v-icon
                :class="{ 'chevron-rotated': isExpanded }"
                size="24"
              >
                {{ icons.mdiChevronDown }}
              </v-icon>
            </td>
            <td class="prevent-report-table__title-col">
              <span class="prevent-report-table__title-name">
                {{ item.companyName }}
              </span>
            </td>
            <td class="prevent-report-table__status-col" />
            <td
              v-for="header in monthHeaders"
              :key="header.value"
              class="prevent-report-table__month-col"
            />
          </tr>
          <tr
            v-for="metric in metricRows"
            :key="`total-${item.companyId}-${metric.key}`"
            class="prevent-report-table__total-row"
          >
            <td class="prevent-report-table__expand-col" />
            <td class="prevent-report-table__title-col">
              <span class="prevent-report-table__metric-label">{{ metric.label }}</span>
            </td>
            <td class="prevent-report-table__status-col">
              <template v-if="metric.subValues.length > 1">
                <span
                  v-for="sub in metric.subValues"
                  :key="sub.label"
                  class="prevent-report-table__submetric-label"
                >{{ sub.label }}</span>
              </template>
            </td>
            <td
              v-for="header in monthHeaders"
              :key="header.value"
              class="prevent-report-table__month-col"
            >
              <template v-if="metric.subValues.length > 1">
                <span
                  v-for="sub in metric.subValues"
                  :key="sub.label"
                  class="prevent-report-table__submetric-value"
                >{{ subValue(item.metricsByMonth, header.value, sub) }}</span>
              </template>
              <template v-else>
                {{ subValue(item.metricsByMonth, header.value, metric.subValues[0]) }}
              </template>
            </td>
          </tr>
        </template>

        <template v-slot:expanded-item="{ item, headers: expandedHeaders }">
          <td
            :colspan="expandedHeaders.length"
            class="prevent-report-table__expanded-cell"
          >
            <v-simple-table dense class="prevent-report-table__subtable">
              <template v-slot:default>
                <tbody>
                  <template v-for="integration in item.integrations">
                    <tr
                      :key="`integration-${integration.id}-header`"
                      class="prevent-report-table__integration-row prevent-report-table__row-group-start"
                    >
                      <td class="prevent-report-table__expand-col" />
                      <td class="prevent-report-table__title-col">
                        <span class="prevent-report-table__integration-label">
                          {{ integrationLabel(integration) }}
                        </span>
                      </td>
                      <td class="prevent-report-table__status-col" />
                      <td
                        v-for="header in monthHeaders"
                        :key="header.value"
                        class="prevent-report-table__month-col"
                      />
                    </tr>
                    <tr
                      v-for="metric in metricRows"
                      :key="`integration-${integration.id}-${metric.key}`"
                      class="prevent-report-table__integration-row"
                    >
                      <td class="prevent-report-table__expand-col" />
                      <td class="prevent-report-table__title-col">
                        <span class="prevent-report-table__metric-label">{{ metric.label }}</span>
                      </td>
                      <td class="prevent-report-table__status-col">
                        <template v-if="metric.subValues.length > 1">
                          <span
                            v-for="sub in metric.subValues"
                            :key="sub.label"
                            class="prevent-report-table__submetric-label"
                          >{{ sub.label }}</span>
                        </template>
                      </td>
                      <td
                        v-for="header in monthHeaders"
                        :key="header.value"
                        class="prevent-report-table__month-col"
                      >
                        <template v-if="metric.subValues.length > 1">
                          <span
                            v-for="sub in metric.subValues"
                            :key="sub.label"
                            class="prevent-report-table__submetric-value"
                          >{{ subValue(integration.metricsByMonth, header.value, sub) }}</span>
                        </template>
                        <template v-else>
                          {{ subValue(integration.metricsByMonth, header.value, metric.subValues[0]) }}
                        </template>
                      </td>
                    </tr>
                  </template>
                </tbody>
              </template>
            </v-simple-table>
          </td>
        </template>
      </v-data-table>
    </v-card>
  </div>
</template>

<script>
import { computed } from '@vue/composition-api';
import usePreventReports from '@/views/prevent/reports/usePreventReports';
import { mdiChevronDown } from '@mdi/js';

export default {
  name: 'PreventReportsView',
  setup() {
    const {
      loading,
      months,
      rows,
      monthHeaders,
      merchantItems,
      metricRows,
      expandedItems,
      integrationLabel,
      subValue,
      fetchReport,
    } = usePreventReports();

    const headers = computed(() => [
      {
        text: 'Merchant',
        value: 'title',
        class: 'prevent-report-table__title-col',
        cellClass: 'prevent-report-table__title-col',
      },
      {
        text: 'Status',
        value: 'status',
        class: 'prevent-report-table__status-col',
        cellClass: 'prevent-report-table__status-col',
      },
      ...monthHeaders.value.map(h => ({
        text: h.text,
        value: h.value,
        class: 'prevent-report-table__month-col',
        cellClass: 'prevent-report-table__month-col',
      })),
    ]);

    return {
      loading,
      months,
      rows,
      monthHeaders,
      merchantItems,
      metricRows,
      expandedItems,
      headers,
      integrationLabel,
      subValue,
      fetchReport,
      icons: {
        mdiChevronDown,
      },
    };
  },
};
</script>

<style lang="scss" scoped>
$column-default-width: 155px;
$column-small-width: 56px;
$column-title-width: 320px;

.prevent-report-table {
  ::v-deep th {
    font-size: 12px;
    white-space: nowrap;
  }

  &__expand-col {
    width: $column-small-width;
    min-width: $column-small-width;
    box-sizing: border-box;
  }

  &__title-col {
    width: $column-title-width;
    min-width: $column-title-width;
    box-sizing: border-box;
  }

  &__status-col {
    width: 110px;
    min-width: 110px;
    box-sizing: border-box;
  }

  &__month-col {
    width: $column-default-width;
    min-width: $column-default-width;
    box-sizing: border-box;
  }

  &__title-name {
    font-weight: 500;
    display: block;
    text-transform: uppercase;
  }

  &__metric-label {
    display: block;
    color: rgba(94, 86, 105, 0.68);
    font-size: 13px;
    line-height: 20px;
  }

  &__integration-row &__metric-label {
    padding-left: 24px;
  }

  &__submetric-label,
  &__submetric-value {
    display: block;
    line-height: 20px;
  }

  &__submetric-label:not(:last-child),
  &__submetric-value:not(:last-child) {
    margin-bottom: 4px;
  }

  &__submetric-label {
    font-size: 13px;
    color: rgba(94, 86, 105, 0.68);
  }

  &__submetric-value {
    font-size: 0.875rem;
    line-height: 1.25rem;
  }

  &__total-row {
    &.actionable {
      cursor: pointer;
    }

    &.prevent-report-table__row-group-start > td {
      background-color: #f5f5f5;
    }
  }

  &__row-group-start > td {
    border-top: 1px solid rgba(94, 86, 105, 0.14);
  }

  &__integration-row {
    background-color: #f9fafb;
  }

  &__integration-label {
    font-weight: 500;
    display: block;
  }

  &__expanded-cell {
    padding: 0 !important;
  }

  &__subtable {
    background-color: transparent;
  }

  ::v-deep .v-icon {
    transition: transform 0.3s ease;
  }
}

.theme--dark .prevent-report-table__integration-row {
  background-color: rgba(231, 227, 252, 0.04);
}

.theme--dark .prevent-report-table__total-row.prevent-report-table__row-group-start > td {
  background-color: rgba(255, 255, 255, 0.04);
}

.chevron-rotated {
  transform: rotate(-180deg);
}
</style>
